import * as React from 'react';
import { StarIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { DISABLED, FOCUS } from '@/components/ui/recipes';

interface RatingProps {
  /** The stars chosen; 0 before any are. */
  value: number;
  onChange: (value: number) => void;
  /** The group's accessible name (Rate your experience). */
  label: string;
  /** How many stars. */
  max?: number;
  disabled?: boolean;
  className?: string;
}

/** Lureo's rating: a row of stars, the chosen ones filled with the call to action's gradient. Keys and screen readers treat it as a radio group. */
export function Rating({ value, onChange, label, max = 5, disabled = false, className }: RatingProps) {
  const gradientId = `rating-${React.useId().replace(/:/g, '')}`;
  const stars = React.useRef<(HTMLButtonElement | null)[]>([]);
  // Tab reaches one star: the chosen one, else the first
  const tabStop = value >= 1 ? value : 1;

  const onKeyDown = (event: React.KeyboardEvent, star: number) => {
    const keys: Record<string, number> = { ArrowRight: star + 1, ArrowUp: star + 1, ArrowLeft: star - 1, ArrowDown: star - 1, Home: 1, End: max };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = Math.min(Math.max(keys[event.key], 1), max);
    onChange(next);
    stars.current[next - 1]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} aria-disabled={disabled || undefined} className={cn('flex gap-1', className)}>
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'var(--lureo-cta)' }} />
            <stop offset="25%" style={{ stopColor: 'var(--lureo-cta)' }} />
            <stop offset="80%" style={{ stopColor: 'var(--lureo-cta-to)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--lureo-cta-to)' }} />
          </linearGradient>
        </defs>
      </svg>
      {Array.from({ length: max }, (_, i) => i + 1).map(star => {
        const filled = value >= star;
        return (
          <button
            key={star}
            ref={el => {
              stars.current[star - 1] = el;
            }}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star === 1 ? '' : 's'}`}
            tabIndex={star === tabStop ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(star)}
            onKeyDown={event => onKeyDown(event, star)}
            className={cn('rounded-lureo-radius-sm p-1', FOCUS, DISABLED)}
          >
            <StarIcon
              weight={filled ? 'fill' : 'regular'}
              className={cn('h-8 w-8 transition-colors sm:h-10 sm:w-10 [&_path]:transition-[fill]', !filled && 'text-lureo-line-strong')}
              style={filled ? { fill: `url(#${gradientId})` } : undefined}
            />
          </button>
        );
      })}
    </div>
  );
}
